import { FarmIcon, J1RightarrowD } from '@e3dc-react/icons'
import { ApiTableColumn, FetchProps } from '@e3dc-react/shell/Elements/ApiView'
import { format, startOfDay, subDays } from '@e3dc-react/shell/Libs/date'
import { Box, Button, IconButton } from '@mui/material'
import { TFunction } from 'i18next'
import React from 'react'
import InstallationLink from 'src/Elements/Installations/InstallationLink'
import InstallationLinkMenuItems from 'src/Elements/Installations/InstallationLinkMenuItems'
import OwnerLink from 'src/Elements/Installations/OwnerLink'
import { InstallationDeviceStateColumn } from 'src/Libs/installation-status'
import { isFlowTheme } from 'src/Libs/utils'
import CollapsedColumnContent from '../components/CollapsedColumnContent'
import { getInstallerName, getOwnerName } from '../installation.functions'
import { getEmsProductImage } from './product-image'
import { Installation } from '../models/installation.model'
import { CustomFunctionalitiesButtonVariant, ImageVersions } from '../types'

const getTypeImage = (item: Installation, productImageClass: string): JSX.Element =>
  item.isFarm ? (
    <Box sx={{ width: 35, height: 35 }}>
      <FarmIcon />
    </Box>
  ) : (
    <img
      src={getEmsProductImage(item.emsMasterProduct, item.installationStatus, ImageVersions.ThreeDimensional)}
      alt=""
      className={productImageClass}
    />
  )

interface GetInstallationTableColumnsResult {
  tableColumnsAll: ApiTableColumn<Installation>[]
  tableColumnsLg: ApiTableColumn<Installation>[]
  tableColumnsMd: ApiTableColumn<Installation>[]
  tableColumnsSm: ApiTableColumn<Installation>[]
}

interface GetInstallationTableColumnsParams {
  t: TFunction
  productTypContainerClass: string
  productImgClass: string
  installationInfoContainerClass: string
  installationInfoTextClass: string
  installationCreatedByContainerClass: string
  showOptionsMenu: boolean
  showStatus: boolean
  showFullActionsMenu?: boolean
  noAccess?: boolean
  isFlowTheme?: boolean
  isPlannedView?: boolean
}

/**
 * return columns for an installation table based on media breakpoints
 */
export const getInstallationTableColumns = ({
  installationCreatedByContainerClass,
  installationInfoContainerClass,
  installationInfoTextClass,
  productImgClass,
  productTypContainerClass,
  showOptionsMenu,
  showStatus,
  t,
  showFullActionsMenu,
  isPlannedView = false,
}: GetInstallationTableColumnsParams): GetInstallationTableColumnsResult => {
  const tableColumnsAll: ApiTableColumn<Installation>[] = [
    {
      name: 'id',
      label: t('installation.overview.installation'),
      value: (item: Installation) => {
        return <InstallationLink installation={item} />
      },
    },
    ...(isPlannedView
      ? [
          {
            name: 'partnersInstallationName' as keyof Installation,
            label: t('installation.overview.installationName'),
            value: (item: Installation) => {
              return item.partnersInstallationName ?? '-'
            },
          },
        ]
      : []),
    {
      name: 'emsMasterDeviceId',
      label: t('administration.installations.serialNumber'),
      value: (item: Installation) => {
        return item.emsMasterDeviceId
      },
    },
    {
      name: 'emsMasterProduct',
      label: t('installation.overview.productTyp'),
      value: (item: Installation) => {
        return (
          <Box className={productTypContainerClass}>
            {getTypeImage(item, productImgClass)}
            {!item.isFarm && item.installationStatus === 'planned' && !item.emsMasterDeviceId && t('installation.overview.qntm')}
            {item.isFarm ? t('installation.overview.farm') : item.emsMasterProduct}
          </Box>
        )
      },
      sortable: false,
    },
    {
      name: 'ownerId',
      label: t('installation.overview.owner'),
      value: (item: Installation) => {
        return <OwnerLink installation={item} />
      },
      sortable: isPlannedView,
    },
  ]

  if (!isPlannedView) {
    tableColumnsAll.push(
      {
        name: 'createdAt',
        label: t('installation.overview.createdAt'),
        value: (item: Installation) => format(new Date(item.createdAt!), t('common.format.date-fns-date')),
      },
      {
        name: 'createdBy',
        label: t('installation.overview.installedBy'),
        value: (item: Installation) => {
          return <Box className={installationCreatedByContainerClass}>{getInstallerName(item)}</Box>
        },
        sortable: false,
      }
    )
  }
  const tableColumnsSm: ApiTableColumn<Installation>[] = [
    {
      name: 'id',
      label: t('installation.overview.installationInfo'),
      value: (item: Installation) => {
        return (
          <Box className={installationInfoContainerClass}>
            {getTypeImage(item, productImgClass)}
            <Box className={installationInfoTextClass}>
              {<InstallationLink installation={item} />}
              {!item.partnersInstallationName ? getOwnerName(item) : null}
            </Box>
            {showOptionsMenu && <InstallationLinkMenuItems installation={item} showFullActionsMenu={showFullActionsMenu} />}
          </Box>
        )
      },
    },
  ]

  const tableColumnStatus: ApiTableColumn<Installation> = {
    name: 'decommissionedAt',
    label: t('installation.overview.status'),
    value: (item: Installation) => <InstallationDeviceStateColumn installation={item} />,
    sortable: true,
  }

  const tableColumnMenu: ApiTableColumn<Installation> = {
    name: 'id',
    label: '',
    value: (item: Installation) => {
      return <InstallationLinkMenuItems installation={item} showFullActionsMenu={showFullActionsMenu} />
    },
    sortable: false,
  }

  if (showStatus) {
    tableColumnsAll.push(tableColumnStatus)
  }

  if (showOptionsMenu) {
    tableColumnsAll.push(tableColumnMenu)
  }

  const tableColumnsAllCopy = [...tableColumnsAll]
  const tableColumnsLg = tableColumnsAllCopy.filter(column => column.name !== 'emsMasterDeviceId')
  const tableColumnsMd = tableColumnsAllCopy.filter(column => column.name !== 'emsMasterDeviceId' && column.name !== 'createdBy')
  return { tableColumnsAll, tableColumnsLg, tableColumnsMd, tableColumnsSm }
}

export const getRequireAttentionCustomFunctionalities = (
  t: TFunction,
  onClick: (installation: Installation) => void,
  variant: CustomFunctionalitiesButtonVariant = 'BUTTON'
): ((installation: Installation) => React.ReactNode)[] => {
  const missingIbnCustomFunctionalities: ((installation: Installation) => React.ReactNode)[] = [
    (installation: Installation) => {
      if (variant === 'BUTTON') {
        return (
          <Button onClick={() => onClick(installation)} key={installation.id}>
            {t('installation.overview.toSystemlog')}
          </Button>
        )
      }
      return (
        <IconButton color="primary" onClick={() => onClick(installation)}>
          <J1RightarrowD key={installation.id} />
        </IconButton>
      )
    },
  ]
  return missingIbnCustomFunctionalities
}

/**
 * Fetch the newest installation: defined as all installations installed in the last seven days
 * @returns fetch props to fetch installations created in the last seven days
 */
export const getNewInstallationFetchProps = (): FetchProps => {
  const sevenDaysAgo = format(startOfDay(subDays(new Date(), 7)), 'yyyy-MM-dd HH:mm:ss')
  return {
    url: 'installations',
    endpoint: process.env.REACT_APP_INSTALLATIONS_ENDPOINT,
    orderBy: '-createdAt',
    filter: `createdAt>=${sevenDaysAgo}`,
  }
}

export const errorInstallationsFetchProps = {
  url: 'installations',
  endpoint: process.env.REACT_APP_INSTALLATIONS_ENDPOINT,
  filter: `${isFlowTheme ? 'appName=ISNULL,' : ''}deviceStatus=IN(error,warning)`,
}

export const allInstallationsFetchProps = {
  url: 'installations',
  endpoint: process.env.REACT_APP_INSTALLATIONS_ENDPOINT,
  orderBy: '-createdAt',
  filter: `${isFlowTheme ? 'appName=ISNULL,' : ''}installationStatus<>planned`,
}

export const allInstallationsWithDecommissionsFetchProps = {
  url: 'installations',
  endpoint: process.env.REACT_APP_INSTALLATIONS_ENDPOINT,
  orderBy: '-createdAt',
  connect: 'installationHistory',
}

export const getAllInstallationAdvancedFilterFetchProps = {
  url: 'installations/advanced-filter',
  endpoint: process.env.REACT_APP_INSTALLATIONS_ENDPOINT,
  orderBy: '-createdAt',
}

export const getPlannedInstallationsFetchProps = {
  url: 'installations',
  endpoint: process.env.REACT_APP_INSTALLATIONS_ENDPOINT,
  orderBy: '-createdAt',
  filter: 'installationStatus=planned',
}

export const getCollapsedTableColumn = (installation: Installation): React.ReactNode => {
  return <CollapsedColumnContent row={installation} />
}
